import {
  Box,
  Flex,
  Heading,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Spinner,
  Text,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { IconBookmark } from 'central-icons/IconBookmark';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import { IconMagnifyingGlass2 } from 'central-icons/IconMagnifyingGlass2';
import { IconPlusLarge } from 'central-icons/IconPlusLarge';
import type { SingleValue } from 'chakra-react-select';
import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';

import { SavedSetsEvents } from '@revelio/core';
import { View } from '@revelio/data-access';

import { useApplySavedFilterSet } from '../../filters/hooks/useApplySavedFilterSet';
import type { Option } from '../../filters/types/filters';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import { filterIdToView } from '../SavedFilterSetButtonV1';
import { CreateFilterSetForm } from '../SavedSets/CreateFilterSetForm';
import { type SavedSetOptionLike, SetsList } from '../SavedSets/SetsList';
import {
  type UserSavedFilterSet,
  useGetStoredSets,
} from '../SavedSets/useGetStoredSets';
import { ActionBarIconButton } from './ActionBarIconButton';

const PlaceholderBox = ({ children }: { children: React.ReactNode }) => (
  <Flex
    direction="column"
    align="center"
    justify="center"
    border="1px dashed"
    borderColor="gray.200"
    borderRadius="sm"
    p={4}
    flexGrow={1}
  >
    {children}
  </Flex>
);

const ModalBackgroundZIndex = 3;

export type SaveFilterSetButtonProps = {
  view: View;
  isDisabled?: boolean;
  onSubmitSavedFilterSet: (
    value: SingleValue<Option<UserSavedFilterSet>> | undefined
  ) => void;
};

const BaseButton = ({
  view,
  onSubmitSavedFilterSet,
  isDisabled,
}: SaveFilterSetButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [stage, setStage] = useState<'browse' | 'create'>('browse');
  const [showSearch, setShowSearch] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (isOpen) onClose();
    },
  });

  useEffect(() => {
    if (stage === 'create') {
      setTimeout(() => {
        const el = document.getElementsByTagName('input')?.[0] as
          | HTMLInputElement
          | undefined;
        el?.focus();
      }, 0);
    }
  }, [stage]);

  const { options: savedSets, fetchingSavedSets, refresh } = useGetStoredSets();

  const compatibleOptions: SavedSetOptionLike[] = useMemo(() => {
    return savedSets
      .filter((set) => set.view === view)
      .map((set) => ({
        value: set.id,
        label: set.name,
        entity: set,
      }));
  }, [savedSets, view]);

  const handleApply = (
    selected: SingleValue<Option<UserSavedFilterSet>> | undefined
  ) => {
    onSubmitSavedFilterSet(selected);
    onClose();

    setTimeout(() => {
      setShowSearch(false);
    }, 500);

    if (selected) {
      mixpanel.track(SavedSetsEvents.APPLY, {
        id: selected.value,
        label: selected.label,
      });
    }
  };

  const handleCreate = () => {
    mixpanel.track(SavedSetsEvents.CREATE);
    refresh();
  };

  const handleUpdate = (item: UserSavedFilterSet) => {
    mixpanel.track(SavedSetsEvents.RENAME, { id: item.id, label: item.name });
    refresh();
  };

  const handleDelete = (item: UserSavedFilterSet) => {
    mixpanel.track(SavedSetsEvents.DELETE, { id: item.id, label: item.name });
    refresh();
  };

  const goToCreateStage = () => setStage('create');
  const goToBrowseStage = () => {
    mixpanel.track(SavedSetsEvents.OPEN);
    setStage('browse');
    onOpen();
  };

  return (
    <Popover
      size="sm"
      isOpen={isOpen}
      onOpen={goToBrowseStage}
      onClose={onClose}
      placement="bottom-end"
      autoFocus={false}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <ActionBarIconButton
          data-testid="saved-set-menu-trigger"
          icon={IconBookmark}
          aria-label="Filter set actions"
          tooltipLabel="Save or Load Filter Set"
          onClick={() => (isOpen ? onClose() : goToBrowseStage())}
          isDisabled={isDisabled}
        />
      </PopoverTrigger>
      <PopoverContent
        zIndex={ModalBackgroundZIndex + 1}
        w="270px"
        maxH="400px"
        ref={containerRef}
      >
        <PopoverHeader borderBottom="none" pt={3} pb={1}>
          <Flex align="center" justify="space-between">
            <Heading fontSize="sm" fontWeight="semibold">
              Filter Sets
            </Heading>
            <Flex align="center" gap={1}>
              {fetchingSavedSets && (
                <Flex width={6} justifyContent="center">
                  <Spinner size="xs" color="green.500" />
                </Flex>
              )}
              {compatibleOptions.length >= 5 && (
                <IconButton
                  aria-label="Toggle search"
                  data-testid="saved-set-search-toggle"
                  icon={<IconMagnifyingGlass2 size={14} />}
                  size="xs"
                  borderRadius="4px"
                  colorScheme={showSearch ? 'gray' : 'navyBlue'}
                  variant={showSearch ? 'solid' : 'ghost'}
                  onClick={() => setShowSearch((prev) => !prev)}
                />
              )}
              <IconButton
                data-testid="saved-set-stage-toggle"
                aria-label="Add Filter Set"
                icon={
                  stage === 'browse' ? (
                    <IconPlusLarge size={14} />
                  ) : (
                    <IconCrossLarge size={14} />
                  )
                }
                size="xs"
                borderRadius="4px"
                colorScheme="navyBlue"
                onClick={stage === 'browse' ? goToCreateStage : goToBrowseStage}
                variant="ghost"
              />
            </Flex>
          </Flex>
        </PopoverHeader>
        <PopoverBody
          display="flex"
          flexDirection="column"
          overflowY="auto"
          pb={3}
          pt={1}
          minH="16rem"
          maxH="32rem"
        >
          {stage === 'browse' && (
            <SetsList
              options={compatibleOptions}
              onApply={handleApply}
              onUpdated={handleUpdate}
              onDeleted={handleDelete}
              showSearch={showSearch}
              emptySlot={
                <PlaceholderBox>
                  <Box color="gray.500" mb={2} fontSize="sm">
                    No compatible sets found
                  </Box>
                  <Link
                    color="lightBlue.600"
                    onClick={goToCreateStage}
                    fontSize="sm"
                  >
                    + Add Filter Set
                  </Link>
                </PlaceholderBox>
              }
              deleteSlot={(opt) => (
                <PlaceholderBox>
                  <Text fontSize="sm" textAlign="center">
                    Are you sure you want to delete <b>{opt.label}</b> saved
                    set? This action cannot be undone.
                  </Text>
                </PlaceholderBox>
              )}
            />
          )}

          {stage === 'create' && (
            <CreateFilterSetForm
              view={view}
              onClose={goToBrowseStage}
              onCreated={handleCreate}
              onUpdated={handleUpdate}
              overwriteSlot={(item) => (
                <PlaceholderBox>
                  <Text fontSize="sm" textAlign="center">
                    Are you sure you want to overwrite <b>{item.name}</b> saved
                    set?
                  </Text>
                </PlaceholderBox>
              )}
            />
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const SaveFilterSetButton = () => {
  const { primaryFilter, revelioPageId } = useTabContentConfig();

  const view = (() => {
    if (revelioPageId === RevelioPageId.COMPANY_SCREENER_V2) {
      return View.CompanyScreener;
    }
    if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER_V2) {
      return View.GeographyScreener;
    }
    if (revelioPageId === RevelioPageId.ROLE_SCREENER_V2) {
      return View.RoleScreener;
    }
    return primaryFilter
      ? filterIdToView(primaryFilter?.id)
      : View.TalentDiscovery;
  })();

  const applySaved = useApplySavedFilterSet();

  return <BaseButton view={view} onSubmitSavedFilterSet={applySaved} />;
};

export const SaveFilterSetButtonLegacy = (props: SaveFilterSetButtonProps) => {
  return (
    <BaseButton
      view={props.view}
      onSubmitSavedFilterSet={props.onSubmitSavedFilterSet}
      isDisabled={props.isDisabled}
    />
  );
};
